/**
 * Admin Project Detail Page — metadata, client assignment, asset upload, share links
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { AssetUploader } from '../../components/admin/AssetUploader';
import { DirectUploadPanel } from '../../components/admin/DirectUploadPanel';
import { LocalImage } from '../../components/common/LocalImage';
import { useFilePicker, FILE_ACCEPTS } from '../../hooks/useFilePicker';
import { uploadToS3 } from '../../services/uploadService';
import type { Project, ModelData, TabId } from '../../../shared/types/project';
import { TABS, PROJECT_CATEGORIES } from '../../../shared/types/project';
import { formatProjectName } from '../../../shared/formatProjectName';
import type { ShareLink, Client } from '../../../shared/types/auth';
import { AddressAutocomplete } from '../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../components/common/AddressAutocomplete';

// Single source of truth for categories lives in shared/types/project.ts
const CATEGORIES = PROJECT_CATEGORIES;

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // Inline form state
  const [form, setForm] = useState<Record<string, any>>({});
  const [nameOverride, setNameOverride] = useState(false);

  const loadProject = useCallback(async () => {
    if (!id) return;
    try {
      const [proj, cls, links] = await Promise.all([
        api.project.get(id),
        api.admin.getClients(),
        api.admin.getShareLinks(id),
      ]);
      setProject(proj);
      setClients(cls);
      setShareLinks(links);
      setForm({
        name: proj.name,
        endClient: proj.endClient === undefined ? null : (proj.endClient || ''),
        city: proj.city || '',
        state: proj.state || '',
        siteNumber: proj.preferences?.siteNumberNA ? null : (proj.siteNumber || ''),
        description: proj.description || '',
        category: proj.category || '',
        address: proj.address || '',
      });
      setNameOverride(proj.preferences?.nameOverride === true);
    } catch (err) {
      console.error('Failed to load project:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // ───── Auto-generated name from project fields ─────
  const clientName = project ? clients.find((c) => c.id === (project as any).clientId)?.name : undefined;
  const autoName = useMemo(() => {
    const sn = form.siteNumber === null ? undefined : (form.siteNumber || undefined);
    return formatProjectName({
      name: 'New Project',
      siteNumber: sn,
      address: form.address || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      clientName,
    } as any);
  }, [clientName, form.siteNumber, form.address, form.city, form.state]);

  const saveMetadata = async () => {
    if (!id || !project) return;
    setSaving(true);
    try {
      const effectiveName = nameOverride ? form.name : autoName;
      const updatedPrefs = {
        ...(project.preferences || {}),
        nameOverride,
        siteNumberNA: form.siteNumber === null,
      };
      const updates = {
        ...form,
        name: effectiveName,
        client: clientName || null,
        siteNumber: form.siteNumber === null ? null : form.siteNumber,
        preferences: updatedPrefs,
      };
      const updated = await api.project.update({ id, updates });
      setProject(updated);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const assignClient = async (clientId: string) => {
    if (!id) return;
    try {
      await api.admin.assignClient(id, clientId || null);
      loadProject();
    } catch (err) {
      console.error('Assign client failed:', err);
    }
  };

  const createShareLink = async () => {
    if (!id) return;
    try {
      const link = await api.admin.createShareLink({ projectId: id, label: 'New link' }) as ShareLink;
      setShareLinks((prev) => [link, ...prev]);
    } catch (err) {
      console.error('Create share link failed:', err);
    }
  };

  const deleteShareLink = async (linkId: string) => {
    try {
      await api.admin.deleteShareLink(linkId);
      setShareLinks((prev) => prev.filter((l) => l.id !== linkId));
    } catch (err) {
      console.error('Delete share link failed:', err);
    }
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading project...</div>;
  if (!project) return <div style={{ color: '#ef4444' }}>Project not found</div>;

  const enabledTabs = project.enabledTabs || [];
  const tabOn = (id: TabId) => enabledTabs.includes(id);

  return (
    <div>
      <div style={styles.header}>
        <Link to="/admin/projects" style={styles.backLink}>&larr; Projects</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={styles.heading}>{formatProjectName(project)}</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={`/admin/projects/${id}/map-editor`} style={{ ...styles.viewBtn, background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>
              Edit Map
            </Link>
            <Link to={`/admin/projects/${id}/view`} style={styles.viewBtn}>
              View Project
            </Link>
          </div>
        </div>
      </div>

      {/* Metadata form */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Project Details</h2>
        <div style={styles.formGrid}>
          <div style={styles.field}>
            <label style={styles.label}>Name</label>
            <input
              type="text"
              value={nameOverride ? form.name : autoName}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{ ...styles.input, opacity: nameOverride ? 1 : 0.7, background: nameOverride ? '#0d0d0d' : '#111' }}
              disabled={!nameOverride}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={nameOverride}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNameOverride(checked);
                  if (checked) setForm((f) => ({ ...f, name: autoName }));
                }}
                style={{ accentColor: '#4F7CFF' }}
              />
              <span style={{ fontSize: 11, color: '#64748b' }}>Override auto-generated name</span>
            </label>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Client</label>
            <div style={{ ...styles.input, background: '#111', color: '#94a3b8', cursor: 'default' }}>
              {clients.find((c) => c.id === (project as any).clientId)?.name || 'Not assigned'}
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>End Client</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                value={form.endClient ?? ''}
                onChange={(e) => setForm({ ...form, endClient: e.target.value })}
                style={{ ...styles.input, flex: 1, opacity: form.endClient === null ? 0.4 : 1 }}
                disabled={form.endClient === null}
                placeholder={form.endClient === null ? 'N/A' : 'End client name'}
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, endClient: form.endClient === null ? '' : null })}
                style={{
                  ...styles.input,
                  width: 'auto',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  background: form.endClient === null ? '#334155' : '#1e293b',
                  color: form.endClient === null ? '#f1f5f9' : '#64748b',
                  border: `1px solid ${form.endClient === null ? '#4F7CFF' : '#333'}`,
                }}
              >
                N/A
              </button>
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Address</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              onSelect={(result: AddressResult) => {
                setForm((f) => ({
                  ...f,
                  address: result.address,
                  city: result.city,
                  state: result.state,
                }));
              }}
              style={styles.input}
            />
          </div>
          <FormField label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <FormField label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
          <div style={styles.field}>
            <label style={styles.label}>Site Number</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                value={form.siteNumber ?? ''}
                onChange={(e) => setForm({ ...form, siteNumber: e.target.value })}
                style={{ ...styles.input, flex: 1, opacity: form.siteNumber === null ? 0.4 : 1 }}
                disabled={form.siteNumber === null}
                placeholder={form.siteNumber === null ? 'N/A' : 'Site/store number'}
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, siteNumber: form.siteNumber === null ? '' : null })}
                style={{
                  ...styles.input,
                  width: 'auto',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  background: form.siteNumber === null ? '#334155' : '#1e293b',
                  color: form.siteNumber === null ? '#f1f5f9' : '#64748b',
                  border: `1px solid ${form.siteNumber === null ? '#4F7CFF' : '#333'}`,
                }}
              >
                N/A
              </button>
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              style={styles.input}
            >
              <option value="">Select...</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Card Thumbnail</label>
          <ThumbnailPicker project={project} onUpdate={loadProject} />
        </div>
        <button onClick={saveMetadata} style={styles.saveBtn} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </section>

      {/* Client assignment */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Client Assignment</h2>
        <select
          value={(project as any).clientId || ''}
          onChange={(e) => assignClient(e.target.value)}
          style={{ ...styles.input, maxWidth: 300 }}
        >
          <option value="">No client assigned</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </section>

      {/* Deliverables toggles */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Deliverables</h2>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
          Toggle which deliverables are visible for this project.
        </p>
        <DeliverableToggles project={project} onUpdate={loadProject} />
      </section>

      {/* Direct data upload: photos, point clouds, tiles → S3 → auto-register */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Upload Data</h2>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 14px' }}>
          Files go straight from your browser to cloud storage and are registered with this project when the upload finishes.
          Large files upload in parallel chunks, so multi-gigabyte point clouds are fine.
        </p>
        <DirectUploadPanel projectId={project.id} onUpdate={loadProject} />
      </section>

      {/* Asset upload sections — only show groups for enabled deliverables */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Assets</h2>

        {tabOn('matterport') && (
          <div style={styles.assetGroup}>
            <h3 style={styles.assetGroupTitle}>Matterport Tours</h3>
            <MatterportManager project={project} onUpdate={loadProject} />
          </div>
        )}

        {tabOn('model') && (
          <>
            <div style={styles.assetGroup}>
              <h3 style={styles.assetGroupTitle}>Autodesk Viewer</h3>
              <AutodeskManager project={project} onUpdate={loadProject} />
            </div>

            <div style={styles.assetGroup}>
              <h3 style={styles.assetGroupTitle}>IFC Files</h3>
              <AssetUploader projectId={project.id} assetType="ifc" accept=".ifc" label="Upload IFC file" />
            </div>
          </>
        )}

        {tabOn('pointcloud') && (
          <div style={styles.assetGroup}>
            <h3 style={styles.assetGroupTitle}>Point Clouds</h3>
            <PotreeCacheStatus projectId={project.id} pointClouds={project.pointClouds || []} onUpdate={loadProject} />
          </div>
        )}

        {tabOn('geotiff') && (
          <>
            <div style={styles.assetGroup}>
              <h3 style={styles.assetGroupTitle}>GeoTIFF / Orthomosaic / DSM</h3>
              <TileCacheStatus projectId={project.id} geotiff={project.geotiff} onUpdate={loadProject} />
            </div>

            <div style={styles.assetGroup}>
              <h3 style={styles.assetGroupTitle}>Photos</h3>
              <PhotoStatus projectId={project.id} />
            </div>
          </>
        )}

        <div style={styles.assetGroup}>
          <h3 style={styles.assetGroupTitle}>PDFs</h3>
          <AssetUploader projectId={project.id} assetType="pdf" accept=".pdf" label="Upload PDF" />
        </div>
      </section>

      {/* Downloadable assets */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Downloadable Assets</h2>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
          Downloads are pushed via the converter API. Toggle which downloads are visible to users.
        </p>
        <DownloadsManager project={project} onUpdate={loadProject} />
      </section>

      {/* Share links */}
      <section style={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={styles.sectionTitle}>Share Links</h2>
          <button onClick={createShareLink} style={styles.smallBtn}>+ New Link</button>
        </div>
        {shareLinks.map((link) => (
          <div key={link.id} style={styles.shareLinkRow}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>{link.label || 'Untitled link'}</div>
              <code style={styles.linkCode}>{window.location.origin}/share/{link.token}</code>
            </div>
            <span style={{ fontSize: 12, color: '#64748b', marginRight: 12 }}>
              {link.viewCount} views
            </span>
            <button onClick={() => deleteShareLink(link.id)} style={styles.deleteBtn}>Delete</button>
          </div>
        ))}
        {shareLinks.length === 0 && (
          <div style={{ color: '#64748b', fontSize: 14 }}>No share links yet</div>
        )}
      </section>

      {/* Danger zone */}
      <section style={{ ...styles.section, borderColor: '#7f1d1d' }}>
        <h2 style={{ ...styles.sectionTitle, color: '#fca5a5' }}>Danger Zone</h2>
        {!showDeleteConfirm ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 500 }}>Delete this project</div>
              <div style={{ color: '#64748b', fontSize: 12 }}>All files, assets, and share links will be permanently removed.</div>
            </div>
            <button onClick={() => setShowDeleteConfirm(true)} style={styles.dangerBtn}>Delete Project</button>
          </div>
        ) : (
          <div>
            <p style={{ color: '#fca5a5', fontSize: 13, margin: '0 0 12px' }}>
              Type <strong style={{ color: '#f1f5f9' }}>{project.name}</strong> to confirm deletion.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={project.name}
                style={{ ...styles.input, flex: 1 }}
                autoFocus
              />
              <button
                onClick={async () => {
                  if (deleteConfirmText !== project.name || !id) return;
                  setDeleting(true);
                  try {
                    await api.project.delete(id);
                    navigate('/admin/projects');
                  } catch (err) {
                    console.error(err);
                    setDeleting(false);
                  }
                }}
                disabled={deleteConfirmText !== project.name || deleting}
                style={{ ...styles.dangerBtn, opacity: deleteConfirmText === project.name ? 1 : 0.4 }}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                style={{ background: 'none', border: '1px solid #475569', borderRadius: 6, color: '#94a3b8', padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ThumbnailPicker({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const { pickFiles } = useFilePicker();
  const [uploading, setUploading] = useState(false);

  const chooseThumbnail = async () => {
    const result = await pickFiles({ accept: FILE_ACCEPTS.IMAGE });
    if (result.canceled || result.files.length === 0) return;
    setUploading(true);
    try {
      const { fileId } = await uploadToS3(result.files[0], project.id, 'thumbnail');
      await api.project.update({ id: project.id, updates: { thumbnailPath: `/api/files/${fileId}/serve` } });
      onUpdate();
    } catch (err) {
      console.error('Thumbnail upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeThumbnail = async () => {
    await api.project.update({ id: project.id, updates: { thumbnailPath: null } });
    onUpdate();
  };

  if (project.thumbnailPath) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <LocalImage
          src={project.thumbnailPath}
          alt="Thumbnail"
          style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={chooseThumbnail} style={styles.smallBtn} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Change'}
          </button>
          <button onClick={removeThumbnail} style={styles.deleteBtn}>Remove</button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={chooseThumbnail} style={styles.smallBtn} disabled={uploading}>
      {uploading ? 'Uploading...' : 'Choose Image'}
    </button>
  );
}

function FormField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
      />
    </div>
  );
}

function MatterportManager({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  const add = async () => {
    if (!url) return;
    const tours = [...(project.matterportTours || []), { id: crypto.randomUUID(), name: name || 'Tour', url }];
    await api.project.update({ id: project.id, updates: { matterportTours: tours } });
    setUrl('');
    setName('');
    onUpdate();
  };

  return (
    <div>
      {project.matterportTours?.map((t) => (
        <div key={t.id} style={{ fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
          {t.name} — <a href={t.url} target="_blank" rel="noreferrer" style={{ color: '#4F7CFF' }}>{t.url}</a>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tour name" style={{ ...styles.input, flex: 1 }} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Matterport URL" style={{ ...styles.input, flex: 2 }} />
        <button onClick={add} style={styles.smallBtn}>Add</button>
      </div>
    </div>
  );
}

function DeliverableToggles({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const enabledTabs = project.enabledTabs || [];

  const toggle = async (tabId: TabId) => {
    const isEnabled = enabledTabs.includes(tabId);
    const updated = isEnabled
      ? enabledTabs.filter((t) => t !== tabId)
      : [...enabledTabs, tabId];
    await api.project.update({ id: project.id, updates: { enabledTabs: updated } });
    onUpdate();
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {TABS.map((tab) => {
        const on = enabledTabs.includes(tab.id);
        return (
          <button
            key={tab.id}
            onClick={() => toggle(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 8,
              border: on ? '1px solid #4F7CFF' : '1px solid #333333',
              background: on ? 'rgba(79,124,255,0.1)' : '#0d0d0d',
              color: on ? '#4F7CFF' : '#64748b',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <span style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              border: on ? '1px solid #4F7CFF' : '1px solid #475569',
              background: on ? '#4F7CFF' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              color: '#fff',
              flexShrink: 0,
            }}>
              {on ? '\u2713' : ''}
            </span>
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(1)} GB`;
}

function DownloadsManager({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const downloads = project.downloads || [];

  const toggleDownload = async (id: string) => {
    const updated = downloads.map((d) =>
      d.id === id ? { ...d, enabled: !d.enabled } : d
    );
    await api.project.update({ id: project.id, updates: { downloads: updated } });
    onUpdate();
  };

  const removeDownload = async (id: string) => {
    const updated = downloads.filter((d) => d.id !== id);
    await api.project.update({ id: project.id, updates: { downloads: updated } });
    onUpdate();
  };

  if (downloads.length === 0) {
    return <div style={{ color: '#64748b', fontSize: 14 }}>No downloads yet — downloads are added via the converter API.</div>;
  }

  return (
    <div>
      {downloads.map((dl) => (
        <div key={dl.id} style={styles.shareLinkRow}>
          {/* Enable/disable toggle */}
          <button
            onClick={() => toggleDownload(dl.id)}
            title={dl.enabled ? 'Visible to users' : 'Hidden from users'}
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              border: dl.enabled ? '1px solid #4F7CFF' : '1px solid #475569',
              background: dl.enabled ? '#4F7CFF' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              color: '#fff',
              flexShrink: 0,
              cursor: 'pointer',
              marginRight: 10,
              padding: 0,
            }}
          >
            {dl.enabled ? '\u2713' : ''}
          </button>

          {/* File type badge */}
          {dl.fileType && (
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#94a3b8',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: 4,
              padding: '2px 6px',
              marginRight: 8,
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              {dl.fileType}
            </span>
          )}

          {/* Name + URL */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 500 }}>{dl.name}</div>
            <a href={dl.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#4F7CFF', wordBreak: 'break-all' }}>
              {dl.url}
            </a>
            {dl.description && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{dl.description}</div>}
          </div>

          {/* File size */}
          {dl.fileSize && (
            <span style={{ fontSize: 12, color: '#64748b', marginRight: 12, flexShrink: 0 }}>
              {formatFileSize(dl.fileSize)}
            </span>
          )}

          <button onClick={() => removeDownload(dl.id)} style={styles.deleteBtn}>Delete</button>
        </div>
      ))}
    </div>
  );
}

function AutodeskManager({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  const externalModels = (project.models || []).filter((m) => m.type === 'external');

  const add = async () => {
    if (!url) return;
    const newModel: ModelData = { id: crypto.randomUUID(), name: name || 'Autodesk Model', type: 'external', externalUrl: url };
    const models = [...(project.models || []), newModel];
    await api.project.update({ id: project.id, updates: { models } });
    setUrl('');
    setName('');
    onUpdate();
  };

  const remove = async (modelId: string) => {
    const models = (project.models || []).filter((m) => m.id !== modelId);
    await api.project.update({ id: project.id, updates: { models } });
    onUpdate();
  };

  return (
    <div>
      {externalModels.map((m) => (
        <div key={m.id} style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
          <span style={{ flex: 1 }}>
            {m.name} — <a href={m.externalUrl} target="_blank" rel="noreferrer" style={{ color: '#4F7CFF' }}>{m.externalUrl}</a>
          </span>
          <button onClick={() => remove(m.id)} style={styles.deleteBtn}>Delete</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Model name" style={{ ...styles.input, flex: 1 }} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Autodesk viewer URL" style={{ ...styles.input, flex: 2 }} />
        <button onClick={add} style={styles.smallBtn}>Add</button>
      </div>
    </div>
  );
}

function PotreeCacheStatus({ projectId, pointClouds, onUpdate }: { projectId: string; pointClouds: any[]; onUpdate: () => void }) {
  const [status, setStatus] = useState<'loading' | 'found' | 'not-found' | 'error'>('loading');
  const [cacheInfo, setCacheInfo] = useState<Array<{ cacheHash: string; name?: string; points?: number }>>([]);
  const [registered, setRegistered] = useState(0);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const detect = async (silent?: boolean) => {
    if (!silent) setStatus('loading');
    try {
      const result = await api.potree.detectCache(projectId);
      setLastChecked(new Date());
      if (result.found && result.cacheEntries) {
        setStatus('found');
        setCacheInfo(result.cacheEntries);
        setRegistered(result.registered || 0);
        if ((result.registered && result.registered > 0) || (result.removed && result.removed > 0)) {
          onUpdate();
        }
      } else {
        setStatus('not-found');
        setCacheInfo([]);
        // Files were removed from S3 — refresh project to clear stale entries
        if (pointClouds.some(pc => pc.cacheHash)) {
          onUpdate();
        }
      }
    } catch {
      if (!silent) setStatus('error');
    }
  };

  // Initial check + poll every 3 minutes
  useEffect(() => {
    detect();
    const interval = setInterval(() => detect(true), 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [projectId]);

  if (status === 'loading') {
    return <div style={{ color: '#94a3b8', fontSize: 13 }}>Checking for Potree files in S3...</div>;
  }

  if (status === 'found') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: '#00cc88', flexShrink: 0, marginTop: 4,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#00cc88', fontSize: 14, fontWeight: 600 }}>
            {cacheInfo.length} point cloud{cacheInfo.length !== 1 ? 's' : ''} found
          </div>
          {cacheInfo.map((ci, i) => (
            <div key={ci.cacheHash} style={{ color: '#94a3b8', fontSize: 12, marginTop: i === 0 ? 4 : 2 }}>
              {ci.name || `Point Cloud ${i + 1}`}
              {ci.points ? ` — ${ci.points.toLocaleString()} pts` : ''}
            </div>
          ))}
          {registered > 0 && (
            <div style={{ color: '#4F7CFF', fontSize: 12, marginTop: 4 }}>
              Auto-registered {registered} new point cloud{registered > 1 ? 's' : ''} from S3
            </div>
          )}
          {lastChecked && (
            <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
              Last checked: {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button onClick={() => detect()} style={{
          background: '#333333', color: '#f1f5f9', border: 'none',
          borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
        }}>
          Re-check
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        background: '#ff4444', flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#ff4444', fontSize: 14, fontWeight: 600 }}>
          No Potree point cloud tile file found
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
          Upload octree files to S3 under projects/{projectId}/potree-cache/
        </div>
        {lastChecked && (
          <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
            Last checked: {lastChecked.toLocaleTimeString()}
          </div>
        )}
      </div>
      <button onClick={() => detect()} style={{
        background: '#333333', color: '#f1f5f9', border: 'none',
        borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
      }}>
        Re-check
      </button>
    </div>
  );
}

function PhotoStatus({ projectId }: { projectId: string }) {
  const [status, setStatus] = useState<'loading' | 'found' | 'not-found' | 'error'>('loading');
  const [counts, setCounts] = useState({ s3Count: 0, registeredCount: 0, placedCount: 0, unplacedCount: 0 });
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const detect = async (silent?: boolean) => {
    if (!silent) setStatus('loading');
    try {
      const result = await api.photo.detectPhotos(projectId);
      setLastChecked(new Date());
      if (result.found || result.registeredCount > 0) {
        setStatus('found');
        setCounts(result);
      } else {
        setStatus('not-found');
        setCounts({ s3Count: 0, registeredCount: 0, placedCount: 0, unplacedCount: 0 });
      }
    } catch {
      if (!silent) setStatus('error');
    }
  };

  useEffect(() => {
    detect();
    const interval = setInterval(() => detect(true), 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [projectId]);

  if (status === 'loading') {
    return <div style={{ color: '#94a3b8', fontSize: 13 }}>Checking for photos in S3...</div>;
  }

  if (status === 'found') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: '#00cc88', flexShrink: 0, marginTop: 4,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#00cc88', fontSize: 14, fontWeight: 600 }}>
            {counts.s3Count} photo{counts.s3Count !== 1 ? 's' : ''} in S3
          </div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
            {counts.registeredCount} registered
            {counts.placedCount > 0 && <span style={{ color: '#00cc88' }}> — {counts.placedCount} placed on map</span>}
            {counts.unplacedCount > 0 && <span style={{ color: '#f59e0b' }}> — {counts.unplacedCount} unplaced</span>}
          </div>
          {counts.s3Count > counts.registeredCount && (
            <div style={{ color: '#4F7CFF', fontSize: 12, marginTop: 2 }}>
              {counts.s3Count - counts.registeredCount} photo{counts.s3Count - counts.registeredCount !== 1 ? 's' : ''} in S3 not yet registered
            </div>
          )}
          {lastChecked && (
            <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
              Last checked: {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button onClick={() => detect()} style={{
          background: '#333333', color: '#f1f5f9', border: 'none',
          borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
        }}>
          Re-check
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        background: '#ff4444', flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#ff4444', fontSize: 14, fontWeight: 600 }}>
          No photos found
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
          Upload photos to S3 under projects/{projectId}/photos/
        </div>
        {lastChecked && (
          <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
            Last checked: {lastChecked.toLocaleTimeString()}
          </div>
        )}
      </div>
      <button onClick={() => detect()} style={{
        background: '#333333', color: '#f1f5f9', border: 'none',
        borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
      }}>
        Re-check
      </button>
    </div>
  );
}

function TileCacheStatus({ projectId, geotiff, onUpdate }: { projectId: string; geotiff: any; onUpdate: () => void }) {
  const [status, setStatus] = useState<'loading' | 'found' | 'not-found' | 'error'>('loading');
  const [cacheInfo, setCacheInfo] = useState<Array<{ cacheHash: string; name?: string; dataType?: string; points?: number; bounds?: boolean }>>([]);
  const [registered, setRegistered] = useState(0);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const detect = async (silent?: boolean) => {
    if (!silent) setStatus('loading');
    try {
      const result = await api.tile.detectCache(projectId);
      setLastChecked(new Date());
      if (result.found && result.cacheEntries) {
        setStatus('found');
        setCacheInfo(result.cacheEntries);
        setRegistered(result.registered || 0);
        if ((result.registered && result.registered > 0) || (result.removed && result.removed > 0)) {
          onUpdate();
        }
      } else {
        setStatus('not-found');
        setCacheInfo([]);
        if (geotiff?.orthoTileCache || geotiff?.dsmTileCache) {
          onUpdate();
        }
      }
    } catch {
      if (!silent) setStatus('error');
    }
  };

  useEffect(() => {
    detect();
    const interval = setInterval(() => detect(true), 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [projectId]);

  if (status === 'loading') {
    return <div style={{ color: '#94a3b8', fontSize: 13 }}>Checking for tile caches in S3...</div>;
  }

  const dataTypeLabel = (dt?: string) => {
    if (dt === 'dsm') return 'DSM';
    if (dt === 'rgb' || dt === 'rgba') return 'Ortho';
    return dt || 'Ortho';
  };

  if (status === 'found') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: '#00cc88', flexShrink: 0, marginTop: 4,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#00cc88', fontSize: 14, fontWeight: 600 }}>
            {cacheInfo.length} tile cache{cacheInfo.length !== 1 ? 's' : ''} found
          </div>
          {cacheInfo.map((ci, i) => (
            <div key={ci.cacheHash} style={{ color: '#94a3b8', fontSize: 12, marginTop: i === 0 ? 4 : 2 }}>
              {dataTypeLabel(ci.dataType)}: {ci.name || 'Untitled'}
              {ci.points ? ` — ${ci.points.toLocaleString()} tiles` : ''}
              {ci.bounds === false && <span style={{ color: '#f59e0b' }}> (no geo bounds)</span>}
            </div>
          ))}
          {registered > 0 && (
            <div style={{ color: '#4F7CFF', fontSize: 12, marginTop: 4 }}>
              Auto-registered {registered} tile cache{registered > 1 ? 's' : ''} from S3
            </div>
          )}
          {lastChecked && (
            <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
              Last checked: {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button onClick={() => detect()} style={{
          background: '#333333', color: '#f1f5f9', border: 'none',
          borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
        }}>
          Re-check
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%',
        background: '#ff4444', flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#ff4444', fontSize: 14, fontWeight: 600 }}>
          No tile caches found
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
          Upload tile caches to S3 under projects/{projectId}/tile-cache/
        </div>
        {lastChecked && (
          <div style={{ color: '#475569', fontSize: 11, marginTop: 2 }}>
            Last checked: {lastChecked.toLocaleTimeString()}
          </div>
        )}
      </div>
      <button onClick={() => detect()} style={{
        background: '#333333', color: '#f1f5f9', border: 'none',
        borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
      }}>
        Re-check
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 24 },
  backLink: {
    color: '#64748b',
    textDecoration: 'none',
    fontSize: 13,
    display: 'inline-block',
    marginBottom: 8,
  },
  heading: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  viewBtn: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
  },
  section: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#f1f5f9', margin: '0 0 16px' },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 16,
  },
  field: { display: 'flex', flexDirection: 'column' as const, gap: 6 },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  input: {
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#f1f5f9',
    fontSize: 13,
    outline: 'none',
  },
  saveBtn: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  assetGroup: {
    padding: '16px 0',
    borderBottom: '1px solid #333333',
  },
  assetGroupTitle: { fontSize: 14, fontWeight: 600, color: '#cbd5e1', margin: '0 0 12px' },
  smallBtn: {
    background: '#333333',
    color: '#f1f5f9',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  shareLinkRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #333333',
  },
  linkCode: {
    fontSize: 12,
    color: '#4F7CFF',
    background: '#0d0d0d',
    padding: '2px 8px',
    borderRadius: 4,
    display: 'inline-block',
    marginTop: 4,
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #7f1d1d',
    color: '#ef4444',
    borderRadius: 6,
    padding: '4px 12px',
    fontSize: 12,
    cursor: 'pointer',
  },
  dangerBtn: {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
};
